import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import UniversalClientDrawer from "./UniversalClientDrawer";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

/**
 * ClientDrawerContext — exposes openClient(client_id) to anywhere in the app.
 *
 * Usage:
 *   const { openClient } = useClientDrawer();
 *   <button onClick={() => openClient(c.client_id)}>{c.company_name}</button>
 */
const ClientDrawerContext = createContext({ openClient: () => {} });

export function ClientDrawerProvider({ children }) {
  const [clientId, setClientId] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);

  useEffect(() => {
    // Use the singleton fetcher (Feb 2026) — coalesces concurrent calls
    // across the whole app and caches for 60s. Fixes the request stampede
    // that caused production 502 cascades.
    import("@/lib/workspaceFetch").then(({ fetchMyWorkspaces }) =>
      fetchMyWorkspaces()
        .then((rows) => rows?.[0] && setWorkspaceId(rows[0].workspace_id))
        .catch(() => {})
    );
  }, []);

  const openClient = useCallback((id) => setClientId(id), []);
  const close = useCallback(() => setClientId(null), []);

  return (
    <ClientDrawerContext.Provider value={{ openClient }}>
      {children}
      <UniversalClientDrawer
        clientId={clientId}
        open={!!clientId}
        onClose={close}
        workspaceId={workspaceId}
      />
    </ClientDrawerContext.Provider>
  );
}

export function useClientDrawer() {
  return useContext(ClientDrawerContext);
}
